import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Form,
    FormInstance,
    Input,
    InputNumber,
    message,
    Row,
    Select,
    Switch,
} from 'antd';
import moment, { Moment } from 'moment';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, RouteComponentProps } from 'react-router-dom';
import divisionApi from '../../../apis/DivisionApi';
import eventApi from '../../../apis/EventApi';
import locationApi from '../../../apis/LocationApi';
import organizationSportsApi from '../../../apis/OrganizationSportsApi';
import sportApi from '../../../apis/SportApi';
import EventsNavigationComponent from '../../../components/EventsNavigationComponent/EventsNavigationComponent';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../context/CustomContext';
import {
    Division,
    EventAthletesAllowed,
    EventRegistration,
    Location,
    NewEvent,
    OrganizationSport,
    Sport,
} from '../../../model/Entities';
import { countries, CountryType, timeZones } from '../../../model/Types';
import HeadMetadata from '../../../services/HeadMetadata';
import notificationService from '../../../services/NotificationService';
import stateService from '../../../services/StateService';
import styles from './EventCreationPage.module.scss';

class EventCreationPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = { sports: [], divisions: [], organizations: [], locations: [] };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'initializing' });

            const responses = await Promise.all([sportApi.listAll(), locationApi.list()]);
            const sports = responses[0];
            const locations = responses[1];

            this.setState({ sports, locations });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    save = async (values: any) => {
        const { allAthletes, catching, pitching } = this.state;
        try {
            this.setState({ loading: 'saving' });

            if (
                (values.generalPrice > 0 &&
                    ((catching && (!values.catchingPrice || values.catchingPrice === 0)) ||
                        (pitching && (!values.pitchingPrice || values.pitchingPrice === 0)))) ||
                (values.catchingPrice > 0 &&
                    (!values.generalPrice ||
                        values.generalPrice === 0 ||
                        (pitching && (!values.pitchingPrice || values.pitchingPrice === 0)))) ||
                (values.pitchingPrice > 0 &&
                    ((catching && (!values.catchingPrice || values.catchingPrice === 0)) ||
                        !values.generalPrice ||
                        values.generalPrice === 0))
            ) {
                message.error(this.props.intl.formatMessage({ id: 'eventCreation.status.freeEvent' }));
            } else {
                const event: NewEvent = Object.assign({}, values);

                const generalRegistration: EventRegistration = { type: 'Global', price: values.generalPrice || 0 };
                const catchingRegistration: EventRegistration = { type: 'Catching', price: values.catchingPrice || 0 };
                const pitchingRegistration: EventRegistration = { type: 'Pitching', price: values.pitchingPrice || 0 };
                event.registrationTypes = [];
                event.registrationTypes.push(generalRegistration);
                catching && event.registrationTypes.push(catchingRegistration);
                pitching && event.registrationTypes.push(pitchingRegistration);

                const athletes: EventAthletesAllowed = { all: allAthletes, divisions: values.divisions };
                event.athletes = athletes;

                event.startDate = values.startDate.valueOf();
                event.endDate = values.endDate ? values.endDate.clone().endOf('day').valueOf() : undefined;

                const eventCreated = await eventApi.create(event);
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

                this.props.history.push(`/events/${eventCreated.id}/summary`);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeSport = async (sportId: number) => {
        const { sports } = this.state;
        const sport = sports.find((s) => s.id === sportId);
        const responses = await Promise.all([
            divisionApi.list(sport!.id!),
            organizationSportsApi.listAll(undefined, sport!.id!),
        ]);
        const divisions = responses[0];
        const organizations = responses[1];
        this.formRef.current?.resetFields([
            'organizationId',
            'divisions',
            'catchingPrice',
            'pitchingPrice',
            'timeZone',
        ]);
        this.setState({ sport, divisions, organizations, catching: false, pitching: false });
    };

    changeLocation = async (locationId: number) => {
        const { locations } = this.state;
        const location = locations.find((l) => l.id === locationId);

        this.formRef.current?.setFieldsValue({
            country: location?.country,
            state: location?.state,
            city: location?.city,
            address: location?.address,
            zipCode: location?.postalCode,
        });

        const country = location?.country;

        this.setState({ country, location });
    };

    changeCountry = (country?: CountryType) => {
        this.formRef.current?.resetFields(['state']);
        this.setState({ country });
    };

    changeCatching = () => {
        const catching = !this.state.catching;
        this.formRef.current?.resetFields(['catchingPrice']);
        this.setState({ catching });
    };

    changePitching = () => {
        const pitching = !this.state.pitching;
        this.formRef.current?.resetFields(['pitchingPrice']);
        this.setState({ pitching });
    };

    changeStartDate = (startDate: Moment | null) => {
        this.formRef.current?.setFieldsValue({ endDate: startDate });
    };

    changeAffiliate = (orgId: string) => {
        const { organizations } = this.state;
        const timeZone = organizations.find((o) => o.organization?.id === orgId)?.organization?.timeZone;
        this.formRef.current?.setFieldsValue({ timeZone: timeZone });
    };

    selectAllAthletes = () => {
        const allAthletes = !this.state.allAthletes;
        this.formRef.current?.resetFields(['divisions']);
        this.setState({ allAthletes });
    };

    /*** COMPONENTS ***/

    renderStates = (): JSX.Element[] | undefined => {
        const { country } = this.state;

        const usaStates = stateService.listUsaStates();
        const canadianStates = stateService.listCanadianStates();

        if (country === 'US') {
            const stateOptions = usaStates.map((state) => (
                <Select.Option key={state.code} value={state.code}>
                    {state.name}
                </Select.Option>
            ));
            return stateOptions;
        } else if (country === 'CA') {
            const stateOptions = canadianStates.map((state) => (
                <Select.Option key={state.name} value={state.name}>
                    {state.name}
                </Select.Option>
            ));
            return stateOptions;
        }
    };

    renderContent = (): React.ReactElement | undefined => {
        const { loading, sports, divisions, sport, organizations, locations, catching, pitching, allAthletes } =
            this.state;

        const sportOptions = sports.map((sport) => (
            <Select.Option key={sport.id} value={sport.id!}>
                {sport.name}
            </Select.Option>
        ));

        const divisionOptions = divisions.map((division) => (
            <Select.Option key={division.id} value={division.id!}>
                {division.name}
            </Select.Option>
        ));

        const organizationOptions = organizations.map((org) => (
            <Select.Option key={org.organization?.id} value={org.organization?.id!}>
                {org.organization?.name}
            </Select.Option>
        ));

        const locationOptions = locations.map((location) => (
            <Select.Option key={location.id} value={location.id!}>
                {location.name}
            </Select.Option>
        ));

        const countryOptions = countries.map((c) => (
            <Select.Option key={c} value={c}>
                <FormattedMessage id={c} />
            </Select.Option>
        ));

        const timeZoneOptions = timeZones.map((t) => (
            <Select.Option key={t} value={t}>
                <FormattedMessage id={t} />
            </Select.Option>
        ));

        return (
            <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical" className={styles.form}>
                <h1>
                    <FormattedMessage id="eventCreation.createEvent" />
                </h1>

                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={18} md={24}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.sport" />}
                            name="sportId"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select className={styles.select} onChange={(value: number) => this.changeSport(value)}>
                                {sportOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={16}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.organization" />}
                            name="organizationId"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select
                                className={styles.select}
                                disabled={sport === undefined}
                                onChange={(value: string) => this.changeAffiliate(value)}
                            >
                                {organizationOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={13} sm={18} md={16}>
                        <Form.Item label={<FormattedMessage id="eventCreation.addDivisions" />} name="divisions">
                            <Select
                                disabled={sport === undefined || allAthletes}
                                className={styles.multiSelect}
                                mode="multiple"
                            >
                                {divisionOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={11} sm={6} md={8}>
                        <Form.Item label=" " name="allAthletes">
                            <Checkbox
                                disabled={sport === undefined}
                                className={styles.marginTop}
                                onChange={this.selectAllAthletes}
                            >
                                <FormattedMessage id="eventCreation.allDivisions" />
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <h2>
                    <FormattedMessage id="eventCreation.location" />
                </h2>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={18} md={16}>
                        <Form.Item label={<FormattedMessage id="eventCreation.eventLocation" />} name="location">
                            <Select onChange={(value: number) => this.changeLocation(value)} allowClear>
                                {locationOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={18} md={12}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.streetAddress" />}
                            name="address"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.city" />}
                            name="city"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.country" />}
                            name="country"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select onChange={(value: CountryType) => this.changeCountry(value)}>
                                {countryOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.state" />}
                            name="state"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select>{this.renderStates()}</Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.zip" />}
                            name="zipCode"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <h2>
                    <FormattedMessage id="eventCreation.dateAndTime" />
                </h2>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={18} md={12}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.startDate" />}
                            name="startDate"
                            rules={[
                                { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || moment().add(-1, 'second') < value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            <FormattedMessage id="eventSettings.status.startDateInvalid" />,
                                        );
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                size="large"
                                use12Hours={true}
                                showTime={{ format: 'hh:mm A' }}
                                format="MM/DD/YYYY hh:mm A"
                                disabledDate={(current) => current.isBefore(moment().add(-1, 'second'))}
                                onChange={this.changeStartDate}
                                minuteStep={15}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={18} md={8}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.timeZone" />}
                            name="timeZone"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select>{timeZoneOptions}</Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={24} sm={18} md={12}>
                        <Form.Item
                            label={<FormattedMessage id="eventCreation.endDate" />}
                            name="endDate"
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('startDate') <= value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            <FormattedMessage id="eventSettings.status.endDateInvalid" />,
                                        );
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                size="large"
                                format="MM/DD/YYYY"
                                disabledDate={(current) =>
                                    current.isBefore(this.formRef.current?.getFieldValue('startDate'))
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <h2>
                    <FormattedMessage id="eventCreation.registration" />
                </h2>
                <Row gutter={[28, 0]} className={styles.labels}>
                    <Col xs={13} sm={8} md={10}>
                        <label>
                            <FormattedMessage id="eventCreation.registrationType" />
                        </label>
                    </Col>
                    <Col xs={11} sm={8} md={6}>
                        <label>
                            <FormattedMessage id="eventCreation.price" />
                        </label>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col xs={13} sm={8} md={10} className={styles.switcherCol}>
                        <FormattedMessage id="eventCreation.general" />
                        <Switch checked={true} />
                    </Col>
                    <Col xs={11} sm={8} md={6}>
                        <Form.Item name="generalPrice">
                            <InputNumber type="number" min={0} maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                {sport?.code !== 'LACROSSE' && (
                    <>
                        <Row gutter={[28, 0]}>
                            <Col xs={13} sm={8} md={10} className={styles.switcherCol}>
                                <FormattedMessage id="eventCreation.catching" />
                                <Switch onChange={() => this.changeCatching()} />
                            </Col>
                            <Col xs={11} sm={8} md={6}>
                                <Form.Item name="catchingPrice">
                                    <InputNumber
                                        type="number"
                                        min={0}
                                        maxLength={100}
                                        size="large"
                                        className={styles.number}
                                        disabled={!catching}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={13} sm={8} md={10} className={styles.switcherCol}>
                                <FormattedMessage id="eventCreation.pitching" />
                                <Switch onChange={() => this.changePitching()} />
                            </Col>
                            <Col xs={11} sm={8} md={6}>
                                <Form.Item name="pitchingPrice">
                                    <InputNumber
                                        type="number"
                                        min={0}
                                        maxLength={100}
                                        size="large"
                                        className={styles.number}
                                        disabled={!pitching}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                <h2>
                    <FormattedMessage id="eventCreation.eventDetails" />
                </h2>
                <Row gutter={[28, 0]}>
                    <Col xs={24} md={24} className={styles.wysiwyg}>
                        <Form.Item name="description" initialValue="">
                            <ReactQuill />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[28, 0]}>
                    <Col xs={24} md={24} className={styles.buttons}>
                        <Link to="/events">
                            <Button size="large" className={styles.btnSecondary}>
                                <FormattedMessage id="button.cancel" />
                            </Button>
                        </Link>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            className={styles.btnMain}
                            loading={loading === 'saving'}
                        >
                            <FormattedMessage id="eventCreation.button.create" />
                        </Button>
                    </Col>
                </Row>
            </Form>
        );
    };

    renderMenu = (collapsed: boolean): React.ReactNode | undefined => {
        return <EventsNavigationComponent collapsed={collapsed} />;
    };

    render() {
        return (
            <>
                <HeadMetadata titleKey="eventCreation.meta.title" />
                <LayoutComponent page="events-upcoming" menu={this.renderMenu} content={this.renderContent} />
            </>
        );
    }
}
export default injectIntl(EventCreationPage);

interface Props extends RouteComponentProps, WrappedComponentProps {}

interface State {
    loading?: 'initializing' | 'saving';
    sports: Sport[];
    sport?: Sport;
    divisions: Division[];
    organizations: OrganizationSport[];
    locations: Location[];
    country?: CountryType;
    catching?: boolean;
    pitching?: boolean;
    allAthletes?: boolean;
    location?: Location;
}
